import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { TranslocoRootModule } from 'app/transloco-root.module';
import { IgntrIconComponent } from 'app/shared/igntr';
import { DashboardCardComponent } from './dashboard-card.component';

export interface AgendaItemView {
  id: string;
  time: string;
  title: string;
  contact: string | null;
  status: 'needs' | 'in' | 'ready';
  value: string | null;
}

@Component({
  selector: 'app-dashboard-today-agenda',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    TranslocoRootModule,
    IgntrIconComponent,
    DashboardCardComponent,
  ],
  template: `
    <app-dashboard-card [title]="'dashAgendaTitle' | transloco" [sub]="subText">
      <a
        card-action
        routerLink="/ai-advanced-pilot"
        class="text-[14px] text-accent-deep inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-accent/10 hover:bg-accent/15 transition-colors"
      >
        <igntr-icon name="sparkle" [size]="14"></igntr-icon>
        {{ 'dashAgendaPrepAll' | transloco }}
      </a>

      @if (loading) {
        <div class="text-[14px] text-slate-400 py-6 text-center">
          {{ 'dashLoading' | transloco }}
        </div>
      } @else if (agenda.length === 0) {
        <div class="text-[14px] text-slate-400 py-6 text-center">
          {{ 'dashAgendaEmpty' | transloco }}
        </div>
      } @else {
        <div class="space-y-[6px]">
          @for (m of agenda; track m.id) {
            <a
              routerLink="/calendar"
              class="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 text-left group transition-colors"
            >
              <div class="text-right shrink-0 w-[44px]">
                <div
                  class="text-[14px] font-mono tabular-nums font-semibold text-slate-900"
                >
                  {{ m.time }}
                </div>
              </div>
              <div
                [class]="
                  'w-1 h-10 rounded-full shrink-0 ' +
                  (m.status === 'needs'
                    ? 'bg-amber-400'
                    : m.status === 'in'
                      ? 'bg-sky-400'
                      : 'bg-emerald-400')
                "
              ></div>
              <div class="flex-1 min-w-0">
                <div class="text-[14px] font-medium text-slate-900 truncate">
                  {{ m.title }}
                </div>
                @if (m.contact || m.value) {
                  <div
                    class="text-[14px] text-slate-500 truncate inline-flex items-center gap-1.5"
                  >
                    @if (m.contact) {
                      <span>{{ m.contact }}</span>
                    }
                    @if (m.contact && m.value) {
                      <span class="text-slate-300">·</span>
                    }
                    @if (m.value) {
                      <span class="font-mono">{{ m.value }}</span>
                    }
                  </div>
                }
              </div>
              <span
                class="opacity-0 group-hover:opacity-100 text-accent transition-opacity"
              >
                <igntr-icon name="arrowR" [size]="16"></igntr-icon>
              </span>
            </a>
          }
        </div>
      }
    </app-dashboard-card>
  `,
})
export class DashboardTodayAgendaComponent {
  @Input() agenda: AgendaItemView[] = [];
  @Input() loading = false;

  get subText(): string {
    const total = this.agenda.length;
    const needs = this.agenda.filter((a) => a.status === 'needs').length;
    return `${total} · ${needs}`;
  }
}
